<script setup lang="ts">
import { ref, computed, watch, nextTick } from "vue";
import { asyncComputed } from '@vueuse/core';
import { useTranslation } from "i18next-vue";

import type { TabItem } from "@/types/ui";

import { useComponentErrorCapture } from "@/mixins/error";
import { getIconName } from "@/mixins/icon";
import { focusInput } from "@/mixins/device";

import TabMenu from "@/components/ui/TabMenu.vue";
import CustomiseRemotePages from "@/components/customise-remote/CustomiseRemotePages.vue";
import ResourceList from "@/components/elements/resources/ResourceList.vue";
import IconList from "@/components/elements/resources/IconList.vue";

const { t } = useTranslation();
useComponentErrorCapture();

const props = defineProps({
  folded: {
    type: Boolean,
    default: false,
  },
});

const defaultTabItem = {
  icon: 'fa-light fa-page',
  label: t('customise_remote.tab.pages'),
  value: 'pages',
};

const tabItems = asyncComputed(async() => {
  const iconPage = await getIconName('fa-page');
  return [
    {
      icon: `fa-light ${iconPage}`,
      label: t('customise_remote.tab.pages'),
      value: 'pages',
    },
    {
      icon: 'fa-light fa-heart',
      label: t('customise_remote.tab.icons'),
      value: 'icons',
    },
    {
      icon: 'fa-light fa-tv',
      label: t('customise_remote.tab.tv_channels'),
      value: 'tv-channels',
    },
    {
      icon: 'fa-light fa-image',
      label: t('customise_remote.tab.backgrounds'),
      value: 'backgrounds',
    },
  ]
});

const animationClass = ref('');
const activeTab = ref<TabItem>(defaultTabItem);
const slideRight = ref(false);
const editView = ref(false);

const resourceList = ref();
const elRemoteTabs = ref();

watch(props, (val) => {
  if (val.folded == true && activeTab.value.value != 'pages') {
    activeTab.value = defaultTabItem;
  } else if (val.folded == true) {
    editView.value = false;
  }
});

watch(activeTab, (val) => {
  if (val.value == 'backgrounds') {
    resourceList.value.loadItems(true);
  }

  nextTick(() => {
    focusInput(elRemoteTabs.value, true)
  })
});

const iconUnfold = asyncComputed(async() => {
  return await getIconName('fa-arrow-up-right-and-arrow-down-left-from-center'); 
});

const mainClasses = computed(() => {
  let classList = "";
  classList += props.folded == true ? "customise-remote__main--folded " : "";
  classList += editView.value == true ? "customise-remote__main--edit-page " : "";
  return classList
});

const tabTransition = computed(() => {
  return slideRight.value == true ? 'slide-tab-right' : 'slide-tab-left';
});

// router.afterEach(async (to, from, next) => {
//   if (to.name == 'customise-remote' && from.name == 'home' && props.folded == false) {
//     animationClass.value = ' growing'
//     await sleep(1000);
//     animationClass.value = '';
//   } else if (to.name == 'home' && from.name == 'customise-remote' && props.folded == true) {
//     animationClass.value = ' shrinking'
//     await sleep(1000);
//     animationClass.value = '';
//     activeTab.value = 'pages';
//   }
// })

function goTo(item: TabItem) {
  const activeTabIndex = tabItems.value.findIndex(i => i.value == activeTab.value.value);
  const newTabIndex = tabItems.value.findIndex(i => i.value == item.value);
  slideRight.value = newTabIndex < activeTabIndex ? true : false;
  activeTab.value = item;
}

function editPage(id: string) {
  editView.value = id && id != null ? true : false;
}

</script>
<template>
  <div class="section-customise-remote" :class="{'section-customise-remote--folded': folded}">
    <Transition name="opacity-fast">
      <div
        v-show="folded == false && editView == false"
        class="customise-remote__navbar">
        <TabMenu
          v-if="tabItems"
          @item-click="goTo"
          :list-data="tabItems"
          :active-tab="activeTab"
        />
      </div>
    </Transition>
    <div class="customise-remote__main" :class="mainClasses + animationClass">
      <Transition name="opacity-fast">
        <div v-show="folded" class="customise-remote__header">
          <h2>{{ $t("home.customise") }}</h2>
          <router-link to="/customise-remote" class="button button--tertiary button--icon button--icon--large">
            <i v-if="iconUnfold" class="fa-light" :class="iconUnfold"></i>
          </router-link>
        </div>
      </Transition>
      <div ref="elRemoteTabs" class="customise-remote__tabs">
        <Transition :name="tabTransition">
          <div v-show="activeTab.value == 'pages'" class="customise-remote__tab">
            <CustomiseRemotePages 
              @edit-page="editPage"
              :folded="folded"
            />
          </div>
        </Transition>
        <Transition :name="tabTransition">
          <div v-show="activeTab.value == 'icons'" class="customise-remote__tab customise-remote__tab--icons">
            <IconList
              :key="'list-icons'"
              :active="activeTab.value == 'icons'"
            />
          </div>
        </Transition>
        <Transition :name="tabTransition">
          <div v-show="activeTab.value == 'tv-channels'" class="customise-remote__tab customise-remote__tab--icons">
            <IconList
              :key="'list-tvchannels'"
              :icon-type="'TvChannelIcon'"
              :active="activeTab.value == 'tv-channels'"
            />
          </div>
        </Transition>
        <Transition :name="tabTransition">
          <div v-show="activeTab.value == 'backgrounds'" class="customise-remote__tab customise-remote__tab--backgrounds">
            <ResourceList
              v-if="folded == false"
              ref="resourceList"
              :title="t('ui.background_images')"
              :allowed-types="['BackgroundImage']"
              :search-full-width="true"
              :active="activeTab.value == 'backgrounds'"
              default-type="BackgroundImage"
            />
          </div>
        </Transition>
      </div>
    </div>
  </div>
</template>